import { getDb } from "@src/db/EpisodesDb";
import { Subscribable } from "./Subscribable";
import type { IAudio, IEpisode, TAudio, TEpisode } from "@src/types/AppTypes";
import axios from "axios";
import Swal from "sweetalert2";
import Cookies from "js-cookie";

export class MusicItem extends Subscribable<() => void> {
  music: IAudio | null = null;
  constructor(item: IAudio | null) {
    super();
    this.music = item;
  }

  setEpisode = (item: IAudio | null) => {
    this.music = item;
    this.notify();
  };

  getMusic = () => {
    return this.music;
  };
}

export class EpisodeItem extends Subscribable<() => void> {
  episode: IEpisode | null = null;
  constructor(item: IEpisode | null) {
    super();
    this.episode = item;
  }

  setEpisode = (item: IEpisode | null) => {
    this.episode = item;
    this.notify();
  };

  getEpisode = () => {
    return this.episode;
  };
}

class IsDownloading extends Subscribable<() => void> {
  //
  isDownloading: boolean;

  constructor(isDownloading: boolean = false) {
    super();
    this.isDownloading = isDownloading;
  }

  setIsDownloading = (value: boolean) => {
    this.isDownloading = value;
    this.notify();
  };

  getIsDownloading = () => {
    return this.isDownloading;
  };
}

export class DownloadingMusicItem extends Subscribable<() => void> {
  isDownloading: IsDownloading = new IsDownloading();
  parts: Blob[] = [];
  id: number;
  start: number = 0;
  type: string;
  end: number = 0;
  item: MusicItem;
  controller: AbortController;
  chunkSize: number;
  progress: number = 0;

  constructor(
    id: number,
    type: string,
    end: number | null,
    item: IAudio | null,
    progress: number,
    isDownloading: boolean = true
  ) {
    super();
    this.id = id;
    this.isDownloading.setIsDownloading(isDownloading);
    this.parts = [];
    this.type = type;
    this.progress = progress;
    this.item = new MusicItem(item);
    this.controller = new AbortController();
    if (item) {
      this.chunkSize = Math.floor(item.bytesize / 100);
      if (end) {
        this.start = end + 1;
        this.end = this.start + this.chunkSize;
      } else {
        this.end = this.chunkSize;
      }
      if (isDownloading) {
        setTimeout(() => {
          this.startDownload();
        });
      }
    } else {
      this.chunkSize = 1024; //1 mb
      this.end = this.chunkSize;
      this.start = 0;
    }
    window.addEventListener("beforeunload", this.handleAbort);
  }

  handleAbort = () => {
    this.controller.abort("manual");
    window.removeEventListener("beforeunload", this.handleAbort);
  };

  removeFromDownloading = async (id: number, removeDb: boolean = false) => {
    await downloadManager.downloadingMusic.removeItem(id, removeDb);
  };

  handleError = (message: string) => {
    Swal.fire({
      text: message,
      icon: "error",
      showConfirmButton: false,
      timer: 3000,
      showCloseButton: true,
      scrollbarPadding: false,
      backdrop: true, // for prevent background scroll when popup is open
    });
  };

  addToDownloadedMusic = async () => {
    try {
      const chunks = await getDb()
        ?.musicBlobData.where("fileId")
        .equals(this.item.getMusic()!.id)
        .sortBy("index");
      if (chunks?.length) {
        const blobs = chunks.map((item) => item.part);
        await getDb()
          ?.musics.update(this.item.getMusic()!.id, {
            blob: new Blob(blobs, {
              type: this.type === "audio/x-wav" ? "audio/wav" : this.type, // safari not support .x-wav content-type in blob.
            }),
          })
          .then(async () => {
            downloadManager.downloadingMusic.removeItem(
              this.item.getMusic()!.id
            );
            chunks.forEach(async () => {
              await getDb()
                ?.musicBlobData?.where("fileId")
                .equals(this.item.getMusic()!.id)
                .delete();
            });
          });
      }
    } catch (error) {
      if (e?.inner?.name === "QuotaExceededError") {
        this.handleError(
          "Download failed. There's not enough browser storage space available."
        );
      } else {
        this.handleError("Download failed.");
      }

      await this.removeFromDownloading(this.item.getMusic()!.id, true);
    }
  };

  downloadChunk = async () => {
    try {
      const res = await axios.get(this.item.getMusic()!.audio, {
        responseType: "blob",
        signal: this.controller.signal,
        headers: {
          range: `bytes=${this.start}-${this.end}`,
        },
      });
      if (res && res.status === 206) {
        await getDb()?.musicBlobData.add({
          part: res.data,
          index: this.progress + 1,
          fileId: this.item.getMusic()!.id,
        });
        await getDb()?.downloadingMusics.update(this.item.getMusic()!.id, {
          progress: this.progress + 1,
          end: this.end,
        });
        this.start = this.end + 1;
        this.end =
          this.end + this.chunkSize > this.item.getMusic()!.bytesize
            ? this.item.getMusic()!.bytesize
            : this.chunkSize + this.end;
        if (this.start < this.item.getMusic()!.bytesize + 1) {
          this.progress += 1;
          this.notify();
          await this.downloadChunk();
        } else {
          this.controller.abort("manual");
          this.notify();
          const d = await getDb()?.downloadingMusics.get(
            this.item.getMusic()!.id
          );
          if (d) this.addToDownloadedMusic();
          await getDb()?.downloadingMusics.delete(this.item.getMusic()!.id);
        }
        this.notify();
      }
    } catch (e) {
      if (e?.code === "ERR_CANCELED") {
        if (
          e?.config?.signal?.reason === "manual" ||
          e?.config?.signal?.reason === "refresh" ||
          e?.config?.signal?.reason === "load"
        )
          return;
      }
      if (e?.inner?.name === "QuotaExceededError") {
        this.handleError(
          "Download failed. There's not enough browser storage space available."
        );
      } else {
        this.handleError("Download failed.");
      }
      await this.removeFromDownloading(this.item.getMusic()!.id, true);
    }
  };

  startDownload = async () => {
    if (this.item.getMusic()) {
      try {
        this.chunkSize = Math.floor(this.item.getMusic()!.bytesize / 100);
        await this.downloadChunk();
      } catch (e) {
        if (e?.inner?.name === "QuotaExceededError") {
          this.handleError(
            "Download failed. There's not enough browser storage space available."
          );
        } else {
          this.handleError("Download failed.");
        }
        await this.removeFromDownloading(this.item.getMusic()!.id, true);
      }
    }
  };

  pauseDownloadInDb = async () => {
    try {
      await getDb()?.downloadingMusics.update(this.item.getMusic()!.id, {
        isDownloading: false,
      });
    } catch (e) {
      if (e?.inner?.name === "QuotaExceededError") {
        this.handleError(
          "Download failed. There's not enough browser storage space available."
        );
      } else {
        this.handleError("Download failed.");
      }
      await this.removeFromDownloading(this.item.getMusic()!.id, true);
    }
  };
  resumeDownloadInDb = async () => {
    try {
      await getDb()?.downloadingMusics.update(this.item.getMusic()!.id, {
        isDownloading: true,
      });
    } catch (e) {
      if (e?.inner?.name === "QuotaExceededError") {
        this.handleError(
          "Download failed. There's not enough browser storage space available."
        );
      } else {
        this.handleError("Download failed.");
      }
      await this.removeFromDownloading(this.item.getMusic()!.id, true);
    }
  };

  pauseDownload = () => {
    this.pauseDownloadInDb();
    this.isDownloading.setIsDownloading(false);
    this.controller.abort("manual");
    downloadManager.notify();
    this.notify();
  };

  resumeDownload = () => {
    this.resumeDownloadInDb();
    this.isDownloading.setIsDownloading(true);
    this.controller = new AbortController();
    this.startDownload();
    downloadManager.notify();
    this.notify();
  };
}

export class DownloadingVideoItem extends Subscribable<() => void> {
  isDownloading: IsDownloading = new IsDownloading();
  id: number;
  start: number = 0;
  type: string;
  end: number = 0;
  item: EpisodeItem;
  controller: AbortController;
  progress: number = 0;
  tsUrls: string[];
  parts: number;

  constructor(
    id: number,
    type: string,
    end: number | null,
    item: IEpisode | null,
    progress: number,
    isDownloading: boolean,
    tsUrls: string[],
    parts: number = 0
  ) {
    super();
    this.id = id;
    this.isDownloading.setIsDownloading(isDownloading);
    this.type = type;
    this.progress = progress;
    this.item = new EpisodeItem(item);
    this.controller = new AbortController();
    this.tsUrls = tsUrls;
    this.parts = parts;
    if (item) {
      if (isDownloading) {
        setTimeout(() => {
          this.startDownload();
        });
      }
    }
    window.addEventListener("beforeunload", this.handleAbort);
  }

  handleAbort = () => {
    this.controller.abort("manual");
    window.removeEventListener("beforeunload", this.handleAbort);
  };

  removeFromDownloading = async (id: number, removeFromDb: boolean = false) => {
    await downloadManager.downloadingEpisodes.removeItem(id, removeFromDb);
  };

  handleError = (message: string) => {
    Swal.fire({
      text: message,
      icon: "error",
      showConfirmButton: false,
      timer: 3000,
      showCloseButton: true,
      scrollbarPadding: false,
      backdrop: true, // for prevent background scroll when popup is open
    });
  };

  addToDownloadedEpisodes = async () => {
    try {
      const chunks = await getDb()
        ?.episodesBlobData.where("fileId")
        .equals(this.item.getEpisode()!.id)
        .sortBy("index");
      if (chunks?.length) {
        await getDb()
          ?.episodes.update(this.item.getEpisode()!.id, {
            id: this.item.getEpisode()!.id,
            blob: [],
            content_type: this.item.getEpisode()!.content_type,
            is_purchased: this.item.getEpisode()!.is_purchased,
            number_of_episode: this.item.getEpisode()!.number_of_episode,
            number_of_season: this.item.getEpisode()!.number_of_season,
            title: this.item.getEpisode()!.title,
            downloading: false,
            downloaded: true,
          })
          .then(async () => {
            navigator.serviceWorker.ready.then(() => {
              if (navigator.serviceWorker.controller) {
                navigator.serviceWorker.controller.postMessage({
                  message: "SyncDB",
                  id: Cookies.get("UIDT") ? Number(Cookies.get("UIDT")) : null,
                });
              }
            });
            downloadManager.downloadingEpisodes.removeItem(
              this.item.getEpisode()!.id
            );
          });
      }
    } catch (error) {
      if (e?.inner?.name === "QuotaExceededError") {
        this.handleError(
          "Download failed. There's not enough browser storage space available."
        );
      } else {
        this.handleError("Download failed.");
      }
      await this.removeFromDownloading(this.item.getEpisode()!.id, true);
    }
  };

  pauseDownloadInDb = async () => {
    try {
      await getDb()?.downloadingVideos.update(this.item.getEpisode()!.id, {
        isDownloading: false,
      });
    } catch (e) {
      if (e?.inner?.name === "QuotaExceededError") {
        this.handleError(
          "Download failed. There's not enough browser storage space available."
        );
      } else {
        this.handleError("Download failed.");
      }
      await this.removeFromDownloading(this.item.getEpisode()!.id, true);
    }
  };
  resumeDownloadInDb = async () => {
    try {
      await getDb()?.downloadingVideos.update(this.item.getEpisode()!.id, {
        isDownloading: true,
      });
    } catch (e) {
      await this.removeFromDownloading(this.item.getEpisode()!.id, true);
    }
  };

  pauseDownload = () => {
    this.pauseDownloadInDb();
    this.isDownloading.setIsDownloading(false);
    this.controller.abort("manual");
    downloadManager.notify();
    this.notify();
  };

  resumeDownload = () => {
    this.resumeDownloadInDb();
    this.isDownloading.setIsDownloading(true);
    this.controller = new AbortController();
    this.startDownload();
    downloadManager.notify();
    this.notify();
  };

  downloadChunk = async () => {
    try {
      const res = await axios.get(
        this.tsUrls[this.parts] + `?cache_bust=${Date.now()}`,
        {
          responseType: "blob",
          signal: this.controller.signal,
        }
      );
      if (res && res.status === 200) {
        await getDb()?.episodesBlobData.add({
          part: res.data,
          index: this.parts,
          fileId: this.item.getEpisode()!.id,
        });
        this.progress = Math.round((this.parts / this.tsUrls.length) * 100);
        await getDb()?.downloadingVideos.update(this.item.getEpisode()!.id, {
          progress: this.progress,
          parts: this.parts,
          end: this.end,
        });
        this.parts++;
        this.notify();
        if (this.parts < this.tsUrls.length) {
          await this.downloadChunk();
        } else {
          this.controller.abort("manual");
          this.notify();
          const d = await getDb()?.downloadingVideos.get(
            this.item.getEpisode()!.id
          );
          if (d) this.addToDownloadedEpisodes();
          await getDb()?.downloadingVideos.delete(this.item.getEpisode()!.id);
        }
        this.notify();
      }
    } catch (e) {
      if (e?.code === "ERR_CANCELED") {
        if (
          e?.config?.signal?.reason === "manual" ||
          e?.config?.signal?.reason === "refresh" ||
          e?.config?.signal?.reason === "load"
        )
          return;
      }
      if (e?.inner?.name === "QuotaExceededError") {
        this.handleError(
          "Download failed. There's not enough browser storage space available."
        );
      } else {
        this.handleError("Download failed.");
      }
      await this.removeFromDownloading(this.item.getEpisode()!.id, true);
    }
  };

  startDownload = async () => {
    if (this.item.getEpisode()) {
      try {
        await this.downloadChunk();
      } catch (e) {
        await this.removeFromDownloading(this.item.getEpisode()!.id, true);
      }
    }
  };
}

class DownloadingItems extends Subscribable<() => void> {
  //
  downloadingEpisodes: Map<number, DownloadingVideoItem> = new Map();

  getDownloadingEpisode = (id: number) => {
    return this.downloadingEpisodes.get(id);
  };

  getDownloadingEpisodes = () => {
    return this.downloadingEpisodes;
  };

  addItem = (item: DownloadingVideoItem) => {
    this.downloadingEpisodes.set(item.id, item);
    this.notify();
  };

  removeEpisodeFromDb = async (id: number) => {
    try {
      await getDb()?.downloadingVideos?.delete(id);
      await getDb()?.episodes?.delete(id);
      await getDb()?.episodesBlobData?.where("fileId").equals(id).delete();
      navigator.serviceWorker.ready.then(() => {
        if (navigator.serviceWorker.controller) {
          navigator.serviceWorker.controller.postMessage({
            message: "SyncDB",
            id: Cookies.get("UIDT") ? Number(Cookies.get("UIDT")) : 0,
          });
        }
      });
    } catch (e) {
      console.log(e);
    }
  };

  removeItem = async (id: number, removeFromDb: boolean = false) => {
    const item = this.downloadingEpisodes.get(id);
    if (item) {
      item.controller.abort("manual");
    }
    this.downloadingEpisodes.delete(id);
    this.notify();
    if (removeFromDb) {
      await this.removeEpisodeFromDb(id);
    }
  };
}

class DownloadingMusicItems extends Subscribable<() => void> {
  downloadingMusic: Map<number, DownloadingMusicItem> = new Map();

  getDownloadingSingleMusic = (id: number) => {
    return this.downloadingMusic.get(id);
  };

  getDownloadingMusic = () => {
    return this.downloadingMusic;
  };

  addItem = (item: DownloadingMusicItem) => {
    this.downloadingMusic.set(item.id, item);
    this.notify();
  };
  removeMusicFromDb = async (id: number) => {
    try {
      await getDb()?.downloadingMusics?.delete(id);
      await getDb()?.musics?.delete(id);
      await getDb()?.musicBlobData?.where("fileId").equals(id).delete();
    } catch (e) {
      console.log(e);
    }
  };

  removeItem = async (id: number, removeFromDb: boolean = false) => {
    const item = this.downloadingMusic.get(id);
    if (item) {
      item.controller.abort("manual");
    }
    this.downloadingMusic.delete(id);
    this.notify();
    if (removeFromDb) {
      await this.removeMusicFromDb(id);
    }
  };
}

export class IsDownloadManagerReady extends Subscribable<() => void> {
  isReady: boolean = false;

  setIsReady = (value: boolean) => {
    this.isReady = value;
    this.notify();
  };

  getIsReady = () => {
    return this.isReady;
  };
}

export class DownloadManager extends Subscribable<() => void> {
  isReady: IsDownloadManagerReady = new IsDownloadManagerReady();
  downloadingEpisodes: DownloadingItems = new DownloadingItems();
  downloadedEpisodes: Map<number, TEpisode> = new Map();
  downloadingMusic: DownloadingMusicItems = new DownloadingMusicItems();
  downloadedMusic: Map<number, TAudio> = new Map();

  getAndSetPendingDownloads = async (callback: () => void = () => {}) => {
    if (!getDb()) return;
    try {
      const downloadingEpisodes = getDb()?.downloadingVideos.toArray();
      const downloadingMusic = getDb()?.downloadingMusics.toArray();
      const downloadedEpisodes = getDb()?.episodes.toArray();
      const downloadedMusic = getDb()?.musics.toArray();
      const episodesBlobs = getDb()?.episodesBlobData.toArray();
      const musicBlobs = getDb()?.musicBlobData.toArray();

      const downloadingEpisodesData = await downloadingEpisodes;
      const downloadingMusicData = await downloadingMusic;
      const downloadedEpisodesData = await downloadedEpisodes;
      const downloadedMusicData = await downloadedMusic;
      const episodesBlobData = await episodesBlobs;
      const musicBlobData = await musicBlobs;

      if (downloadingEpisodesData?.length) {
        downloadingEpisodesData.forEach(async (item) => {
          const parts = episodesBlobData?.filter(
            (blob) => blob.fileId === item.id
          );
          if (parts && parts?.length > item.progress) {
            parts
              .filter((blob) => blob.index > item.progress)
              .forEach(async (blob) => {
                await getDb()
                  ?.episodesBlobData?.where("fileId")
                  .equals(blob.fileId)
                  .and((t) => t.index > item.progress)
                  .delete();
              });
          }
        });
      }
      if (downloadedEpisodesData?.length) {
        downloadedEpisodesData.forEach((item) => {
          this.downloadedEpisodes.set(item.id, item);
        });
      }
      if (downloadingMusicData?.length) {
        downloadingMusicData.forEach(async (item) => {
          const parts = musicBlobData?.filter(
            (blob) => blob.fileId === item.id
          );
          if (parts && parts?.length > item.progress) {
            parts
              .filter((blob) => blob.index > item.progress)
              .forEach(async (blob) => {
                await getDb()
                  ?.musicBlobData?.where("fileId")
                  .equals(blob.fileId)
                  .and((t) => t.index > item.progress)
                  .delete();
              });
          }
          this.downloadingMusic.addItem(
            new DownloadingMusicItem(
              item.id,
              item.type,
              item.end,
              null,
              item.progress,
              item.isDownloading
            )
          );
        });
      }
      if (downloadedMusicData?.length) {
        downloadedMusicData.forEach((item) => {
          this.downloadedMusic.set(item.id, item);
        });
      }
      callback();
    } catch (e) {
      console.log(e);
    }
  };

  addEpisodeDownload = (
    item: IEpisode,
    tsUrls: string[],
    end: number | null = null,
    progress: number = 0,
    isDownloading: boolean = true,
    parts: number = 0
  ) => {
    this.downloadingEpisodes.addItem(
      new DownloadingVideoItem(
        item.id,
        item.content_type,
        end,
        item,
        progress,
        isDownloading,
        tsUrls,
        parts
      )
    );
  };

  addMusicDownload = (
    item: IAudio,
    end: number | null = null,
    progress: number = 0,
    isDownloading: boolean = true
  ) => {
    this.downloadingMusic.addItem(
      new DownloadingMusicItem(
        item.id,
        item.content_type,
        end,
        item,
        progress,
        isDownloading
      )
    );
  };

  getDownloadingEpisodes = () => {
    return this.downloadingEpisodes;
  };

  getDownloadedEpisodes = () => {
    return this.downloadedEpisodes;
  };
  //   onMount = async () => {};
}

export const downloadManager = new DownloadManager();
